// @ts-strict-ignore
import {Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from "@angular/core";
import {EventEmitterService} from "@event-emitter";
import {SimCameraService, SimConfigurationService, SimUtilityService} from "@services";
import {CameraProxyStatus, ConnectivityState} from "@models";

@Component({
	selector: "app-mse-video",
	standalone: true,
	imports: [],
	templateUrl: "./mse-video.component.html",
	styleUrl: "./mse-video.component.scss",
})
export class MseVideoComponent implements OnInit, OnChanges, OnDestroy {
	ConnectivityState = ConnectivityState;
	CameraProxyStatus = CameraProxyStatus;

	@ViewChild("videoElement", {static: true})
	videoElement: ElementRef<HTMLVideoElement>;

	@Input() mseStreamId = "";
	@Input() mseProxyUrl = "";
	@Input() mseProxyStatus: CameraProxyStatus;
	@Input() mseVideoStyle = "";
	@Input() mseVideoClass = "";

	@Input() sourceVideoOptions: {
		cameraId: string;
		ipAddress: string;
		url: string;
		anonymousAccessAllowed: boolean;
		status: ConnectivityState;
	} = null;

	private isActive: boolean = true;

	public settings = SimConfigurationService.settings;

	public videoId = "";
	public deviceStatus: ConnectivityState;
	public currentState: "Ready" | "Offline" | "Loading";
	private isPlayingStream = false;

	private streamId: string = null;

	private checkInterval: ReturnType<typeof setInterval> = null;

	private mse: MediaSource;
	private mseSourceBuffer: any;
	private mseQueue: any[] = [];
	private mseStreamingStarted: boolean = false;
	private ws: WebSocket = null;

	constructor(
		private _simCameraService: SimCameraService,
		private _eventEmitter: EventEmitterService,
	) {
		this.videoId = SimUtilityService.createGuid();
	}

	public ngOnInit(): void {
		this._eventEmitter.onSystemHubReconnected.subscribe(async () => {
			await this.checkCameraStatus();
		});

		this._eventEmitter.onCameraStateChanged.subscribe(async (args) => {
			if (this.mseStreamId === args.cameraState.id) {
				this.updateCameraStatus(args.cameraState);
			}
		});

		this.checkInterval = setInterval(async () => {
			this.checkConnectivity();
		}, 1000);
	}

	public canPlay(event: Event) {
		this.currentState = "Ready";
		this.videoElement.nativeElement.play();
	}

	public playing(event: Event) {
		this.isPlayingStream = true;
	}

	public async ngOnChanges(changesObj: SimpleChanges) {
		const streamId = changesObj["mseStreamId"];

		if (streamId) {
			this.cleanup(true);
			await this.checkCameraStatus();
		}
	}

	public ngOnDestroy() {
		clearInterval(this.checkInterval);
		this.cleanup(true);
	}

	private isReadyToStart(): boolean {
		return (
			this.isActive &&
			!!this.mseStreamId &&
			this.deviceStatus === ConnectivityState.Online &&
			this.mseProxyStatus === CameraProxyStatus.Available
		);
	}

	private checkConnectivity() {
		if (
			this.isPlayingStream &&
			(this.deviceStatus === ConnectivityState.Offline || this.mseProxyStatus !== CameraProxyStatus.Available)
		) {
			this.cleanup(true);
		} else {
			// Check if we should attempt opening the stream
			if (!this.isPlayingStream && this.isReadyToStart() && this.currentState !== "Loading") {
				this.currentState = "Loading";
				setTimeout(() => {
					if (this.currentState == "Loading") this.currentState = "Offline";
				}, 5000);

				this.streamId = this.mseStreamId;

				this.startStream();
			}
		}
	}

	private async checkCameraStatus() {
		let result = await this._simCameraService.getCameraById(this.mseStreamId);
		this.updateCameraStatus(result);
	}

	private updateCameraStatus(cameraState: {proxyStatus: CameraProxyStatus; status: ConnectivityState}) {
		if (this.deviceStatus !== cameraState.status || this.mseProxyStatus !== cameraState.proxyStatus) {
			this.mseProxyStatus = cameraState.proxyStatus;
			this.deviceStatus = cameraState.status;

			this.checkConnectivity();
		}
	}

	private startStream() {
		if (this.mse) {
			this.cleanup(true);
		}
		this.tryConnection();
	}

	private tryConnection() {
		this.cleanup();

		console.log("trying connection, active: " + this.isActive);
		if (this.isActive) {
			this.playMse();
		}
	}

	private cleanup(includeConnection: boolean = false) {
		if (this.mse && includeConnection) {
			this.mse.removeAllListeners();
			if (this.mseSourceBuffer) {
				this.mse.removeSourceBuffer(this.mseSourceBuffer);
			}
			this.mse = null;
			this.mseSourceBuffer = null;
		}

		this.videoElement.nativeElement.src = null;

		if (this.ws) {
			this.ws.close();
			this.ws = null;
		}

		this.isPlayingStream = false;
	}

	@HostListener("document:visibilitychange")
	public $onVisibilityChange(): any {
		if (window.document.visibilityState === "visible") {
			//console.info("Reconnecting MSE Player because the tab is being shown")
			this.currentState = "Loading";
			this.isActive = true;
			this.tryConnection();
		} else if (window.document.visibilityState === "hidden") {
			//console.info("Disconnecting MSE Player because the tab was hidden");
			this.currentState = "Offline";
			this.isActive = false;
			this.cleanup(true);
		}
	}

	private getPlayUrl() {
		let path = `${this.mseProxyUrl}/stream/${this.streamId}/channel/0/mse?uuid=${this.streamId}&channel=0`;
		return path.replace("http", "ws");
	}

	private playMse() {
		let _this = this;
		this.mse = new MediaSource();
		this.videoElement.nativeElement.src = window.URL.createObjectURL(this.mse);
		this.mse.addEventListener(
			"sourceopen",
			function () {
				_this.ws = new WebSocket(_this.getPlayUrl());
				_this.ws.binaryType = "arraybuffer";

				_this.ws.onmessage = function (event) {
					let data = new Uint8Array(event.data);
					if (data[0] == 9) {
						let decoded_arr = data.slice(1);
						let mimeCodec = "";
						if (window.TextDecoder) {
							mimeCodec = new TextDecoder("utf-8").decode(decoded_arr);
						} else {
							//mimeCodec = Utf8ArrayToStr(decoded_arr);
						}
						_this.mseSourceBuffer = _this.mse.addSourceBuffer('video/mp4; codecs="' + mimeCodec + '"');
						_this.mseSourceBuffer.mode = "segments";
						_this.mseSourceBuffer.addEventListener("updateend", _this.pushPacket());
					} else {
						_this.readPacket(event.data);
					}
				};
			},
			false,
		);
	}

	private readPacket(packet: any) {
		if (!this.mseStreamingStarted) {
			this.mseSourceBuffer.appendBuffer(packet);
			this.mseStreamingStarted = true;
			return;
		}
		this.mseQueue.push(packet);

		if (!this.mseSourceBuffer.updating) {
			this.pushPacket();
		}
	}

	private pushPacket() {
		let _this = this;
		if (!_this.mseSourceBuffer.updating) {
			if (_this.mseQueue.length > 0) {
				let packet = _this.mseQueue.shift();
				let view = new Uint8Array(packet);
				_this.mseSourceBuffer.appendBuffer(packet);
			} else {
				_this.mseStreamingStarted = false;
			}
		}
	}
}
